/**
 * LeadsContext — what every lead view shares, now that no screen loads the
 * whole lead list (paging 2/4).
 *
 * It used to hold ONE copy of every lead for All Clients and the top-bar
 * search, so the two could not age differently. That list stopped at
 * PostgREST's 1,000-row cap. The lists now page on the server
 * (GET /leads/search), and what has to stay shared is FRESHNESS:
 *
 *   version / refresh   A counter. Anything that CREATES, REASSIGNS or edits a
 *                       lead calls refresh(); every lead view lists `version` in
 *                       its fetch dependencies and refetches. (The old failure:
 *                       the top bar mounted once at login and never saw a lead
 *                       created after it.)
 *   useLeadLabels(ids)  Names and job numbers for a known set of leads, from
 *                       POST /leads/lookup, cached here so screens asking for
 *                       the same leads share one request. Cleared on refresh().
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient } from '../hooks/useApi';
import { useAuth } from './AuthContext';
import type { LeadLabel } from '../services/api';

interface LeadsValue {
  /** Bumps whenever a lead changes; list it in a lead view's fetch dependencies. */
  version: number;
  refresh: () => void;
  /** @internal shared label cache for useLeadLabels */
  labels: Map<string, LeadLabel>;
}

const LeadsContext = createContext<LeadsValue>({ version: 0, refresh: () => {}, labels: new Map() });

export function LeadsProvider({ children }: { children: React.ReactNode }) {
  const [version, setVersion] = useState(0);
  const labels = useRef(new Map<string, LeadLabel>());
  const refresh = useCallback(() => {
    labels.current = new Map();
    setVersion((v) => v + 1);
  }, []);
  const value = useMemo<LeadsValue>(() => ({ version, refresh, labels: labels.current }), [version, refresh]);
  return <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>;
}

export function useLeads(): Pick<LeadsValue, 'version' | 'refresh'> {
  const { version, refresh } = useContext(LeadsContext);
  return { version, refresh };
}

/**
 * Labels (job number, name, stage) for the given lead ids, fetched once each
 * and shared. Returns a Map; ids not loaded yet are simply absent.
 */
export function useLeadLabels(ids: (string | null | undefined)[]): Map<string, LeadLabel> {
  const { labels, version } = useContext(LeadsContext);
  const { token } = useAuth();
  const api = useApiClient();
  const [, rerender] = useState(0);
  const key = [...new Set(ids.filter(Boolean) as string[])].sort().join(',');

  useEffect(() => {
    if (!token || !key) return;
    const missing = key.split(',').filter((id) => !labels.has(id));
    if (!missing.length) return;
    let alive = true;
    api.lookupLeads(missing)
      .then((r) => {
        for (const l of r.data ?? []) labels.set(String(l.id), l);
        if (alive) rerender((n) => n + 1);
      })
      .catch(() => { /* a missing label renders as an em-dash; the screen still works */ });
    return () => { alive = false; };
  }, [api, token, key, labels, version]);

  return labels;
}
